
// Shared controller for managing project "labels" (ProjectTagType rows) at a single scope. Mirrors the
// LogoSlotController pattern: a plain class (NOT a pict provider) driven by a host provider, exporting the
// shared list template (LABEL_TEMPLATES) + CSS (LABEL_CSS). Template handlers call back into the host by
// name via Record.ProviderName, so one template serves both hosts.
//
// Two scopes, same entity:
//   - customer DEFAULT labels: IDProject = 0,  IDCustomer = <customer>   (seed new projects; My Headlight)
//   - per-PROJECT labels:      IDProject = <p>, IDCustomer = <customer>  (Project workspace "Labels" tab)
// ProjectTagType is standard Meadow REST; `Name` is the display name (falls back to `TagType` when blank).

const libConfigSync = require('./ConfigSync.js');

class LabelManagerController
{
	/**
	 * @param {any} pPict
	 * @param {String} pProviderName - the host provider's identifier (the list template calls it back).
	 */
	constructor(pPict, pProviderName)
	{
		this.pict = pPict;
		this.providerName = pProviderName;

		this.scope = { IDProject: 0, IDCustomer: 0 };
		this.canManage = false;
		// The project "Labels" tab offers a one-click "copy customer defaults"; My Headlight (which IS the
		// defaults) does not.
		this.showCopyDefaults = false;
		this._onChange = () => {};

		this.labels = [];
		this.loading = false;
		this.busy = false;
		// Add-row input state (stashed on every keystroke WITHOUT a re-render so the inputs keep focus).
		this.newTagType = '';
		this.newName = '';
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	get modal()
	{
		return this.pict.views['Pict-Section-Modal'];
	}

	_toast(pMessage, pType)
	{
		if (this.modal && (typeof this.modal.toast === 'function')) { this.modal.toast(pMessage, { type: pType || 'success' }); }
	}

	/**
	 * After a label-type change, (debounced) publish this scope's config to the field apps (Fieldbook) — a
	 * Project sync for a project scope, a Customer sync for the customer-default scope (My Headlight). Without
	 * this, a new/renamed/removed type shows here but never reaches Fieldbook. Debounced + permission-gated in
	 * ConfigSync (a non-super-user without Module-PlatformConfiguration-Access gets a gentle notice instead).
	 */
	_requestConfigSync()
	{
		if (Number(this.scope.IDProject) > 0)
		{
			libConfigSync.requestSync(this.pict, 'Project', this.scope.IDProject);
		}
		else if (Number(this.scope.IDCustomer) > 0)
		{
			libConfigSync.requestSync(this.pict, 'Customer', this.scope.IDCustomer);
		}
	}

	// --- REST (promisified) ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_create(pRecord)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.createEntity('ProjectTagType', pRecord, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_update(pRecord)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.updateEntity('ProjectTagType', pRecord, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_delete(pID)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.deleteEntity('ProjectTagType', pID, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/**
	 * Point the controller at a scope + permission and register the host's re-render.
	 * @param {{IDProject:Number, IDCustomer:Number}} pScope
	 * @param {Boolean} pCanManage
	 * @param {Function} pOnChange
	 * @param {Boolean} [pShowCopyDefaults]
	 */
	setContext(pScope, pCanManage, pOnChange, pShowCopyDefaults)
	{
		this.scope = pScope || { IDProject: 0, IDCustomer: 0 };
		this.canManage = !!pCanManage;
		this.showCopyDefaults = !!pShowCopyDefaults;
		this._onChange = (typeof pOnChange === 'function') ? pOnChange : () => {};
		this.labels = [];
		this.newTagType = '';
		this.newName = '';
	}

	_sort()
	{
		this.labels.sort((a, b) => (Number(a.Ordinal || 0) - Number(b.Ordinal || 0)) || String(a.TagType || '').localeCompare(String(b.TagType || '')));
	}

	_sortRows(pRows)
	{
		return (Array.isArray(pRows) ? pRows.slice() : []).sort((a, b) => (Number(a.Ordinal || 0) - Number(b.Ordinal || 0)) || String(a.TagType || '').localeCompare(String(b.TagType || '')));
	}

	/**
	 * Resolve the effective DEFAULT label-type set for a customer: the customer's own defaults
	 * (IDProject=0, IDCustomer=c) when present, else Headlight's platform defaults (IDProject=0,
	 * IDCustomer=0). Mirrors the ProjectObservationManifestJoin cascade already shipping in
	 * ProjectWorkspace-Provider and the legacy Projects app (project → customer → platform).
	 * @param {Number} pIDCustomer
	 * @return {Promise<{Rows: Array<Object>, Source: ('customer'|'platform'|'none')}>}
	 */
	async _resolveEffectiveDefaults(pIDCustomer)
	{
		if (Number(pIDCustomer) !== 0)
		{
			const tmpCustomer = this._sortRows(await this._getJSON(`ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~${pIDCustomer}/0/1000`));
			if (tmpCustomer.length > 0) { return { Rows: tmpCustomer, Source: 'customer' }; }
		}
		const tmpPlatform = this._sortRows(await this._getJSON('ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~0~FBV~IDCustomer~EQ~0/0/1000'));
		return { Rows: tmpPlatform, Source: tmpPlatform.length > 0 ? 'platform' : 'none' };
	}

	/**
	 * Materialize the given default rows as scoped copies at the current scope (skipping any TagType
	 * already present), pushing the created rows onto `this.labels`. Shared by copyDefaults() and the
	 * seed-on-first-customization in add().
	 * @param {Array<Object>} pRows
	 * @return {Promise<Number>} the number of rows created.
	 */
	async _seedRows(pRows)
	{
		const tmpExisting = new Set(this.labels.map((pLabel) => String(pLabel.TagType || '').toLowerCase()));
		let tmpAdded = 0;
		for (let i = 0; i < (Array.isArray(pRows) ? pRows.length : 0); i++)
		{
			const tmpRow = pRows[i];
			const tmpKey = String(tmpRow.TagType || '').toLowerCase();
			if (!tmpKey || tmpExisting.has(tmpKey)) { continue; }
			try
			{
				const tmpSaved = await this._create({ TagType: tmpRow.TagType, Name: tmpRow.Name || tmpRow.TagType, Ordinal: tmpRow.Ordinal || 0, IDProject: this.scope.IDProject, IDCustomer: this.scope.IDCustomer });
				if (tmpSaved && tmpSaved.IDProjectTagType) { this.labels.push(tmpSaved); tmpExisting.add(tmpKey); tmpAdded++; }
			}
			catch (pError) { /* keep going; the caller reports the total */ }
		}
		this._sort();
		return tmpAdded;
	}

	// --- Data ops ---

	async load()
	{
		this.loading = true;
		this._onChange();
		try
		{
			const tmpRows = await this._getJSON(`ProjectTagTypes/FilteredTo/FBV~IDProject~EQ~${this.scope.IDProject}~FBV~IDCustomer~EQ~${this.scope.IDCustomer}/0/1000`);
			this.labels = Array.isArray(tmpRows) ? tmpRows.slice() : [];
			this._sort();
			// No own rows → fall back to the effective default set (customer defaults, else Headlight's
			// platform defaults), shown read-only, so the tab reflects what the project (or, in My Headlight,
			// the customer) actually inherits until it is customized. Mirrors the manifest cascade + the legacy
			// Projects app: project → customer(0,c) → platform(0,0).
			this._showingDefaults = false;
			this._defaults = [];
			this._defaultsSource = '';
			if (this.labels.length === 0)
			{
				const tmpEffective = await this._resolveEffectiveDefaults(this.scope.IDCustomer);
				this._defaults = tmpEffective.Rows;
				this._defaultsSource = tmpEffective.Source;
				this._showingDefaults = this._defaults.length > 0;
			}
		}
		catch (pError)
		{
			this.labels = [];
			this._defaults = [];
			this._showingDefaults = false;
			this._toast(`Could not load labels: ${pError.message || pError}`, 'error');
		}
		this.loading = false;
		this._onChange();
	}

	/** Stash an add-row field WITHOUT a re-render (keeps the input focused while typing). */
	setNew(pField, pValue)
	{
		if (pField === 'TagType') { this.newTagType = pValue; }
		else { this.newName = pValue; }
	}

	async add()
	{
		if (!this.canManage) { return; }
		const tmpTagType = String(this.newTagType || '').trim();
		if (!tmpTagType) { this._toast('Enter a label type.', 'error'); return; }
		// While inheriting, the visible set is the read-only defaults (this.labels is still empty), so
		// dedupe the new type against those too.
		const tmpInheriting = this._showingDefaults === true;
		const tmpVisible = tmpInheriting ? (this._defaults || []) : this.labels;
		if (tmpVisible.some((pLabel) => String(pLabel.TagType || '').toLowerCase() === tmpTagType.toLowerCase()))
		{
			this._toast('That label type already exists in this set.', 'error');
			return;
		}
		const tmpName = String(this.newName || '').trim() || tmpTagType;
		this.busy = true;
		this._onChange();
		try
		{
			// First customization of an inheriting project: materialize the whole inherited default set as
			// scoped rows FIRST, so adding one custom type doesn't drop the rest (the reported bug — mirrors
			// the legacy Projects app's seed-on-first-edit).
			if (tmpInheriting)
			{
				await this._seedRows(this._defaults);
				this._showingDefaults = false;
				this._defaults = [];
			}
			const tmpNextOrdinal = this.labels.reduce((pMax, pLabel) => Math.max(pMax, Number(pLabel.Ordinal) || 0), 0) + 1;
			const tmpSaved = await this._create({ TagType: tmpTagType, Name: tmpName, Ordinal: tmpNextOrdinal, IDProject: this.scope.IDProject, IDCustomer: this.scope.IDCustomer });
			if (!tmpSaved || !tmpSaved.IDProjectTagType) { throw new Error((tmpSaved && tmpSaved.Error) || 'the server returned an unexpected response.'); }
			this.labels.push(tmpSaved);
			this._sort();
			this.newTagType = '';
			this.newName = '';
			this._toast(`Added the “${tmpTagType}” label type.`);
			this._requestConfigSync();
		}
		catch (pError)
		{
			this._toast(`Could not add the label type: ${pError.message || pError}`, 'error');
		}
		this.busy = false;
		this._onChange();
	}

	async updateName(pID, pValue)
	{
		if (!this.canManage) { return; }
		const tmpLabel = this.labels.find((pLabel) => pLabel.IDProjectTagType === pID);
		if (!tmpLabel) { return; }
		const tmpValue = String(pValue || '').trim() || tmpLabel.TagType;
		if (tmpValue === tmpLabel.Name) { return; }
		try
		{
			const tmpSaved = await this._update({ IDProjectTagType: pID, Name: tmpValue });
			if (tmpSaved && tmpSaved.ErrorCode) { throw new Error(tmpSaved.Error || 'the server rejected the update.'); }
			tmpLabel.Name = tmpValue;
			this._toast('Label name saved.');
			this._requestConfigSync();
		}
		catch (pError)
		{
			this._toast(`Could not save the label name: ${pError.message || pError}`, 'error');
		}
		this._onChange();
	}

	async remove(pID)
	{
		if (!this.canManage) { return; }
		const tmpLabel = this.labels.find((pLabel) => pLabel.IDProjectTagType === pID);
		if (!tmpLabel) { return; }
		const fApply = async () =>
		{
			try
			{
				await this._delete(pID);
				this.labels = this.labels.filter((pLabel) => pLabel.IDProjectTagType !== pID);
				this._toast(`Removed the “${tmpLabel.TagType}” label type.`);
				this._requestConfigSync();
			}
			catch (pError)
			{
				this._toast(`Could not remove the label type: ${pError.message || pError}`, 'error');
			}
			this._onChange();
		};
		// Warn when the type is IN USE — i.e. labels (values) of this type exist. Removing the type is a
		// hard delete that does NOT remove those labels; they are left without a defined type. (Per the ask:
		// warn for in-use TYPES; there is no such warning when removing an individual label value.)
		const tmpInUse = await this._countTypeUsage(tmpLabel.TagType);
		const tmpTypeName = (tmpLabel.Name != null && String(tmpLabel.Name).trim() !== '') ? tmpLabel.Name : tmpLabel.TagType;
		const tmpScopeText = (Number(this.scope.IDProject) !== 0) ? 'on this project' : 'across your projects';
		const tmpMessage = (tmpInUse > 0)
			? `The “${tmpTypeName}” label type is in use by ${tmpInUse} label${tmpInUse === 1 ? '' : 's'} ${tmpScopeText}. Removing the type won’t delete those labels, but they’ll be left without a defined type. Remove it anyway?`
			: `Remove the “${tmpTypeName}” label type?`;
		if (this.modal && (typeof this.modal.confirm === 'function'))
		{
			this.modal.confirm(tmpMessage,
				{ title: 'Remove label type', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true })
				.then((pOk) => { if (pOk) { fApply(); } });
		}
		else { fApply(); }
	}

	/**
	 * Count the label VALUES (ObservationTagProjectJoin rows) that use a given TagType in this scope — the
	 * "in use" signal for a type removal. Project scope counts the project's labels of that type; the
	 * customer-default scope (My Headlight, IDProject=0) counts labels of that type across the customer's
	 * real projects (IDProject>0). Returns 0 if the count can't be fetched (never blocks the removal).
	 * @param {String} pTagType
	 * @return {Promise<Number>}
	 */
	async _countTypeUsage(pTagType)
	{
		try
		{
			const tmpType = encodeURIComponent(pTagType);
			const tmpFilter = (Number(this.scope.IDProject) !== 0)
				? `FBV~IDProject~EQ~${this.scope.IDProject}~FBV~TagType~EQ~${tmpType}`
				: `FBV~IDCustomer~EQ~${this.scope.IDCustomer}~FBV~TagType~EQ~${tmpType}~FBV~IDProject~GT~0`;
			const tmpCount = await this._getJSON(`ObservationTagProjectJoins/Count/FilteredTo/${tmpFilter}`);
			const tmpValue = (tmpCount && (tmpCount.Count != null)) ? tmpCount.Count : tmpCount;
			return Number(tmpValue) || 0;
		}
		catch (pError)
		{
			return 0;
		}
	}

	/** Project scope only: create scoped copies of the effective default label types (customer → platform). */
	async copyDefaults()
	{
		if (!this.canManage) { return; }
		try
		{
			const tmpEffective = await this._resolveEffectiveDefaults(this.scope.IDCustomer);
			const tmpExisting = new Set(this.labels.map((pLabel) => String(pLabel.TagType || '').toLowerCase()));
			const tmpToCreate = tmpEffective.Rows.filter((pDefault) => !tmpExisting.has(String(pDefault.TagType || '').toLowerCase()));
			if (tmpToCreate.length === 0) { this._toast('All default label types are already present.'); return; }
			this.busy = true;
			this._onChange();
			const tmpAdded = await this._seedRows(tmpToCreate);
			// Copying while inheriting materializes the set — drop the read-only defaults view.
			if (this._showingDefaults) { this._showingDefaults = false; this._defaults = []; }
			this._toast(`Added ${tmpAdded} label type${tmpAdded === 1 ? '' : 's'} from the defaults.`);
			if (tmpAdded > 0) { this._requestConfigSync(); }
		}
		catch (pError)
		{
			this._toast(`Could not copy the default label types: ${pError.message || pError}`, 'error');
		}
		this.busy = false;
		this._onChange();
	}

	// --- Display ---

	_escape(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));
	}

	/** The inherited-defaults banner text, worded for the source tier (customer vs Headlight platform). */
	_defaultsBannerText()
	{
		const tmpProjectScope = Number(this.scope.IDProject) !== 0;
		const tmpAction = 'Add a label type or use “Copy default labels” to give it a custom set.';
		if (this._defaultsSource === 'platform')
		{
			return tmpProjectScope
				? `Showing Headlight’s platform default label types — neither this project nor your organization has set its own yet. ${ tmpAction }`
				: 'Showing Headlight’s platform default label types — your organization hasn’t set its own yet. Add a label type or copy these to give your organization a custom set.';
		}
		return tmpProjectScope
			? `Showing your organization’s default label types — this project hasn’t set its own yet. ${ tmpAction }`
			: 'Showing your organization’s default label types.';
	}

	/** Build the render record the shared Headlight-LabelManager template consumes. */
	buildState()
	{
		// When the project has no labels of its own, show the customer defaults read-only (the fallback set
		// the project actually uses). Rows are never editable/removable in this mode — the user customizes
		// by adding a label or copying the defaults, which makes a project-scoped set on the next load.
		const tmpUsingDefaults = (this._showingDefaults === true) && Array.isArray(this._defaults) && (this._defaults.length > 0);
		const tmpSource = tmpUsingDefaults ? this._defaults : this.labels;
		const tmpRows = tmpSource.map((pLabel) =>
		{
			const tmpNameText = (pLabel.Name != null && String(pLabel.Name).trim() !== '') ? pLabel.Name : pLabel.TagType;
			const tmpRowEditable = this.canManage && !tmpUsingDefaults;
			return {
				ProviderName: this.providerName,
				ID: pLabel.IDProjectTagType,
				// Lower-cased "type + display name" for the client-side type search (matched by the filter input's
				// inline handler against this data attribute — no re-render, so the input keeps focus).
				FilterText: this._escape(`${pLabel.TagType || ''} ${tmpNameText || ''}`.toLowerCase()),
				TagType: this._escape(pLabel.TagType),
				EditSlot: tmpRowEditable ? [ { ProviderName: this.providerName, ID: pLabel.IDProjectTagType, Name: this._escape(pLabel.Name != null ? pLabel.Name : '') } ] : [],
				ROSlot: !tmpRowEditable ? [ { NameText: this._escape(tmpNameText) } ] : [],
				RemoveSlot: (tmpRowEditable && !this.busy) ? [ { ProviderName: this.providerName, ID: pLabel.IDProjectTagType } ] : [],
			};
		});
		const tmpHasTable = (tmpRows.length > 0) || this.canManage;
		return {
			ProviderName: this.providerName,
			LoadingSlot: this.loading ? [ {} ] : [],
			DefaultsBanner: tmpUsingDefaults ? [ { BannerText: this._defaultsBannerText() } ] : [],
			// A client-side type search — shown once there are enough rows to be worth filtering. It hides/shows
			// the already-loaded rows in place (no server round-trip, no re-render), so it keeps focus while typing.
			FilterSlot: (!this.loading && tmpRows.length > 5) ? [ {} ] : [],
			TableSlot: (!this.loading && tmpHasTable) ? [ {
				Labels: tmpRows,
				AddSlot: (this.canManage && !this.busy) ? [ { ProviderName: this.providerName, NewTagType: this._escape(this.newTagType), NewName: this._escape(this.newName) } ] : [],
				BusySlot: this.busy ? [ {} ] : [],
			} ] : [],
			EmptySlot: (!this.loading && tmpRows.length === 0 && !this.canManage) ? [ {} ] : [],
			CopyDefaultsSlot: (this.showCopyDefaults && this.canManage && !this.loading && !this.busy) ? [ { ProviderName: this.providerName } ] : [],
			ReadOnlySlot: (!this.canManage && !this.loading) ? [ {} ] : [],
		};
	}
}

module.exports = LabelManagerController;

module.exports.LABEL_CSS = /*css*/`
	.lbl-mgr { font-size: 0.92rem; }
	.lbl-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; overflow: hidden; }
	.lbl-table th { text-align: left; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686);
		background: var(--theme-color-background-tertiary, #f4f6f9); padding: 0.45rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); }
	.lbl-filter { display: flex; align-items: center; gap: 0.35rem; margin: 0 0 0.7rem; }
	.lbl-filter-icon { color: var(--theme-color-text-muted, #6b7686); }
	.lbl-filter-input { flex: 1 1 auto; max-width: 22rem; }
	.lbl-th-type { width: 32%; }
	.lbl-th-act { width: 1%; }
	.lbl-table td { padding: 0.45rem 0.8rem; border-bottom: 1px solid var(--theme-color-border-light, #eef1f5); vertical-align: middle; }
	.lbl-table tbody tr:last-child td { border-bottom: none; }
	.lbl-type { font-weight: 650; color: var(--theme-color-text-primary, #1f2733); }
	.lbl-name-ro { color: var(--theme-color-text-secondary, #45505f); }
	.lbl-input { max-width: 22rem; }
	.lbl-act { text-align: right; white-space: nowrap; }
	.lbl-add td { background: var(--theme-color-background-tertiary, #fafbfc); }
	.lbl-empty { color: var(--theme-color-text-muted, #6b7686); padding: 0.6rem 0.2rem; }
	.lbl-ro-note { font-size: 0.84rem; color: var(--theme-color-text-muted, #6b7686); font-style: italic; margin-top: 0.6rem; }
	.lbl-copy { margin-top: 0.8rem; }
	.lbl-defaults-banner { display: flex; align-items: center; gap: 0.5rem; font-size: 0.85rem; color: var(--theme-color-text-secondary, #45505f);
		background: var(--theme-color-background-selected, #eef1f8); border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; padding: 0.6rem 0.85rem; margin: 0 0 0.85rem; line-height: 1.5; }
	.lbl-defaults-banner .icon { color: var(--theme-color-brand-primary, #5650e6); flex: 0 0 auto; }
	.lbl-loading { color: var(--theme-color-text-muted, #6b7686); padding: 0.8rem 0.2rem; }
	.lbl-spin { display: inline-block; width: 1rem; height: 1rem; border: 2px solid var(--theme-color-border-default, #dbdbdb); border-top-color: var(--theme-color-brand-primary, #5650e6); border-radius: 50%; animation: lbl-spin 0.7s linear infinite; vertical-align: -0.15rem; margin-right: 0.4rem; }
	@keyframes lbl-spin { to { transform: rotate(360deg); } }
`;

module.exports.LABEL_TEMPLATES =
[
	{
		Hash: 'Headlight-LabelManager',
		Template: /*html*/`
			<div class="lbl-mgr">
				{~TS:Headlight-LabelLoading:Record.LoadingSlot~}
				{~TS:Headlight-LabelDefaultsBanner:Record.DefaultsBanner~}
				{~TS:Headlight-LabelFilter:Record.FilterSlot~}
				{~TS:Headlight-LabelTable:Record.TableSlot~}
				<p class="lbl-empty lbl-filter-none" style="display:none">No label types match your search.</p>
				{~TS:Headlight-LabelEmpty:Record.EmptySlot~}
				{~TS:Headlight-LabelCopyDefaults:Record.CopyDefaultsSlot~}
				{~TS:Headlight-LabelReadOnly:Record.ReadOnlySlot~}
			</div>`
	},
	{
		Hash: 'Headlight-LabelFilter',
		// A pure client-side filter over the already-loaded rows: it toggles row visibility by the row's
		// data-lbl attribute, keeping input focus (no re-render). Self-contained so it needs no host delegate
		// and works identically in every host that renders this template.
		Template: /*html*/`
			<div class="lbl-filter">
				<span class="icon lbl-filter-icon"><i class="fas fa-magnifying-glass"></i></span>
				<input class="input is-small lbl-filter-input" type="text" placeholder="Search label types…" oninput="(function(pInput){var tmpQuery=pInput.value.trim().toLowerCase();var tmpBox=pInput.closest('.lbl-mgr');if(!tmpBox){return;}var tmpShown=0,tmpTotal=0;tmpBox.querySelectorAll('tr.lbl-row').forEach(function(pRow){tmpTotal++;var tmpHay=pRow.getAttribute('data-lbl')||pRow.textContent.toLowerCase();var tmpHit=(!tmpQuery)||(tmpHay.indexOf(tmpQuery)>=0);pRow.style.display=tmpHit?'':'none';if(tmpHit){tmpShown++;}});var tmpNone=tmpBox.querySelector('.lbl-filter-none');if(tmpNone){tmpNone.style.display=(tmpQuery&&tmpShown===0&&tmpTotal>0)?'':'none';}})(this)" />
			</div>`
	},
	{
		Hash: 'Headlight-LabelDefaultsBanner',
		Template: /*html*/`<div class="lbl-defaults-banner"><span class="icon"><i class="fas fa-circle-info"></i></span><span>{~D:Record.BannerText~}</span></div>`
	},
	{
		Hash: 'Headlight-LabelTable',
		Template: /*html*/`
			<table class="lbl-table">
				<thead><tr><th class="lbl-th-type">Type</th><th>Display name</th><th class="lbl-th-act"></th></tr></thead>
				<tbody>
					{~TS:Headlight-LabelRow:Record.Labels~}
					{~TS:Headlight-LabelAdd:Record.AddSlot~}
				</tbody>
			</table>`
	},
	{
		Hash: 'Headlight-LabelRow',
		Template: /*html*/`
			<tr class="lbl-row" data-lbl="{~D:Record.FilterText~}">
				<td class="lbl-type">{~D:Record.TagType~}</td>
				<td>{~TS:Headlight-LabelNameEdit:Record.EditSlot~}{~TS:Headlight-LabelNameRO:Record.ROSlot~}</td>
				<td class="lbl-act">{~TS:Headlight-LabelRemove:Record.RemoveSlot~}</td>
			</tr>`
	},
	{
		Hash: 'Headlight-LabelNameEdit',
		Template: /*html*/`<input class="input lbl-input" type="text" value="{~D:Record.Name~}" onchange="_Pict.providers['{~D:Record.ProviderName~}'].labelUpdateName({~D:Record.ID~}, this.value)" />`
	},
	{
		Hash: 'Headlight-LabelNameRO',
		Template: /*html*/`<span class="lbl-name-ro">{~D:Record.NameText~}</span>`
	},
	{
		Hash: 'Headlight-LabelRemove',
		Template: /*html*/`<button class="button is-light is-small" title="Remove label" onclick="_Pict.providers['{~D:Record.ProviderName~}'].labelRemove({~D:Record.ID~})"><span class="icon"><i class="fas fa-trash"></i></span></button>`
	},
	{
		Hash: 'Headlight-LabelAdd',
		Template: /*html*/`
			<tr class="lbl-add">
				<td><input class="input is-small lbl-input" type="text" placeholder="New type (e.g. Phase)" value="{~D:Record.NewTagType~}" oninput="_Pict.providers['{~D:Record.ProviderName~}'].labelSetNew('TagType', this.value)" /></td>
				<td><input class="input is-small lbl-input" type="text" placeholder="Display name (optional)" value="{~D:Record.NewName~}" oninput="_Pict.providers['{~D:Record.ProviderName~}'].labelSetNew('Name', this.value)" /></td>
				<td class="lbl-act"><button class="button is-link is-small" onclick="_Pict.providers['{~D:Record.ProviderName~}'].labelAdd()"><span class="icon"><i class="fas fa-plus"></i></span><span>Add</span></button></td>
			</tr>`
	},
	{
		Hash: 'Headlight-LabelCopyDefaults',
		Template: /*html*/`<button class="button is-light is-small lbl-copy" onclick="_Pict.providers['{~D:Record.ProviderName~}'].labelCopyDefaults()"><span class="icon"><i class="fas fa-copy"></i></span><span>Copy default labels</span></button>`
	},
	{
		Hash: 'Headlight-LabelEmpty',
		Template: /*html*/`<p class="lbl-empty">No labels have been defined yet.</p>`
	},
	{
		Hash: 'Headlight-LabelReadOnly',
		Template: /*html*/`<p class="lbl-ro-note">You don’t have permission to change these labels.</p>`
	},
	{
		Hash: 'Headlight-LabelLoading',
		Template: /*html*/`<div class="lbl-loading"><span class="lbl-spin"></span>Loading labels…</div>`
	}
];
